import { CloseIcon } from "@chakra-ui/icons";
import { Box, Divider, Flex, IconButton, Portal } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import ErrorPage from "../../../../shared/components/ErrorPage";
import LoadingPage from "../../../../shared/components/LoadingPage";
import useApi from "../../../../shared/hooks/useApi";
import useAuthData from "../../../../shared/hooks/useAuthInfo";
import { NewRnPlatformEmailPopupProps } from "../../../../shared/hooks/useNewRnPlatformEmail";
import MaximizeIcon from "../../../../shared/icons/MaximizeIcon";
import MinimizeIcon from "../../../../shared/icons/MinimizeIcon";
import { queryKeys } from "../../../../shared/query-keys";
import { getFullName } from "../../../../shared/utils/get-full-name";
import {
  formatMessageWithPlaceholders,
  RnPlatformEmailFooters,
  RnPlatformEmailPlaceHolders,
  RnPlatformEmails,
} from "../../utils/rn-platform-communication.utils";
import { NewTicketPopUpBody } from "../NewTicketPopup";

function NewRnPlatformEmailPopup(props: NewRnPlatformEmailPopupProps & { onClose: () => void }) {
  const [isMinimized, setIsMinimized] = React.useState(false);

  return (
    <Portal>
      <Box
        bg="white"
        borderTopRadius="2xl"
        bottom={39}
        boxShadow="0 0 16px -2px rgba(0,0,0,0.25)"
        id="comm-center-new-ticket-popup"
        position="fixed"
        right={55}
        sx={{
          "--max-chat-height": "60vh",
        }}
        transform={isMinimized ? `translateY(calc(100% - 49px)) ` : undefined}
        transition="all 250ms ease"
        w={isMinimized ? 300 : 1000}
        zIndex="popover"
      >
        <Flex p={2}>
          <IconButton
            aria-label="close"
            borderRadius="xl"
            icon={<CloseIcon fontSize={12} />}
            variant="ghost"
            onClick={props.onClose}
          />
          <IconButton
            aria-label="close"
            borderRadius="xl"
            hidden={isMinimized}
            icon={<MinimizeIcon fontSize={18} />}
            variant="ghost"
            onClick={() => setIsMinimized(true)}
          />
          <IconButton
            aria-label="close"
            borderRadius="xl"
            hidden={!isMinimized}
            icon={<MaximizeIcon fontSize={18} />}
            variant="ghost"
            onClick={() => setIsMinimized(false)}
          />
        </Flex>

        <Divider />

        <Box maxH={700} overflowY="auto">
          <NewRnPlatformEmailPopupBody {...props} />
        </Box>
      </Box>
    </Portal>
  );
}

function NewRnPlatformEmailPopupBody(
  props: NewRnPlatformEmailPopupProps & { onClose: () => void }
) {
  const { api } = useApi();
  const { agencyMember } = useAuthData();

  const patientQuery = useQuery({
    queryKey: queryKeys.patient.get(props.defaultValues.patientId!),
    queryFn: () => {
      return api.get("/agencies/:agencyId/patients/:patientId", {
        path: { patientId: props.defaultValues.patientId! },
      });
    },
  });

  const documents = useQuery({
    enabled: props.patientDocumentIds !== undefined && props.defaultValues.patientId !== null,
    queryKey: queryKeys.patient.documents(props.patientDocumentIds ?? []),
    queryFn: () =>
      api.post("./patients/:patientId/scheduled_documents", {
        body: {
          documentIds: props.patientDocumentIds ?? [],
        },
        path: {
          patientId: props.defaultValues.patientId!,
        },
      }),
    select: (response) => response.documents,
  });

  const agencyData = useQuery({
    enabled: props.defaultValues.patientId !== null,
    queryKey: queryKeys.patient.agency(props.defaultValues.patientId!),
    queryFn: () =>
      api.get("./patients/:patientId/agency", {
        path: {
          patientId: props.defaultValues.patientId!,
        },
      }),
    select: (response) => response.agency,
  });

  if (agencyData.isPending || documents.isPending || patientQuery.isPending) {
    return <LoadingPage />;
  }

  if (agencyData.isError) {
    return <ErrorPage error={agencyData.error} resetErrorBoundary={agencyData.refetch} />;
  }

  if (documents.isError) {
    return <ErrorPage error={documents.error} resetErrorBoundary={documents.refetch} />;
  }

  if (patientQuery.isError) {
    return <ErrorPage error={patientQuery.error} resetErrorBoundary={patientQuery.refetch} />;
  }

  const replacements = {
    [RnPlatformEmailPlaceHolders.patientName]: getFullName(patientQuery.data),
    [RnPlatformEmailPlaceHolders.outsourcedAgencyName]: agencyData.data.name,
    [RnPlatformEmailPlaceHolders.agencyMemberName]: getFullName(agencyMember),
  };

  const subject = formatMessageWithPlaceholders(
    RnPlatformEmails.sendPatientDocuments.subject,
    replacements
  );
  const message = formatMessageWithPlaceholders(
    RnPlatformEmails.sendPatientDocuments.content,
    replacements
  );
  const content = message + RnPlatformEmailFooters.confidentialityNotice;

  return (
    <NewTicketPopUpBody
      {...props}
      defaultEmailValues={{
        ...props.defaultEmailValues,
        subject,
        content,
        attachments: documents.data.map((doc) => ({
          key: `${doc.title}-${doc.id}`,
          s3Url: doc.fileUrl,
          name: `${doc.title}.${doc.fileExtension}`,
          type: doc.fileType,
        })),
      }}
      onClose={props.onClose}
    />
  );
}

export default NewRnPlatformEmailPopup;
